import { supabase, isSupabaseConfigured, syncRealtimeAuth, ensureValidSession } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import { flowStep, mark, measure, traceAsync } from "@/lib/perf";
import type { AuthUser, AuthResult, LoginInput } from "@/types/auth";

const AUTH_TIMEOUTS = {
    signInMs: 10000,
    dbLookupMs: 3500,
    metadataWriteMs: 2500,
};

/**
 * Login — aligned mit Mobile authService.ts (Lines 260-380)
 *
 * Flow:
 * 1. signInWithPassword
 * 2. Role aus users-Tabelle, Fallback: user_metadata.role
 * 3. practiceId aus user_metadata, Fallback: practices-Tabelle, Fallback: practice_staff
 * 4. Email-Verifikation ist KEIN Hard-Block (wie in Mobile)
 */
export async function login(input: LoginInput): Promise<AuthResult> {
    const startedAt = Date.now();
    if (!isSupabaseConfigured()) {
        logLoginTiming("not_configured", startedAt);
        return {
            success: false,
            error: "Supabase ist nicht konfiguriert. Bitte VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY setzen.",
        };
    }

    try {
        logger.info("[Auth] Login-Versuch gestartet");
        flowStep('login', 'auth_service_login_start');

        mark('auth_signin_start');
        const { data, error } = await traceAsync(
            'auth.signInWithPassword',
            () => withTimeout(
                supabase.auth.signInWithPassword({
                    email: input.email,
                    password: input.password,
                }),
                AUTH_TIMEOUTS.signInMs,
                "auth.signInWithPassword",
            ),
            { timeoutMs: AUTH_TIMEOUTS.signInMs },
        );
        mark('auth_signin_end');
        measure('auth_signin_ms', 'auth_signin_start', 'auth_signin_end');

        if (error) {
            console.error("[Auth] Login-Fehler:", error.message);
            flowStep('login', 'auth_service_signin_error', { reason: error.message });
            logLoginTiming("sign_in_error", startedAt);
            return { success: false, error: mapAuthError(error.message) };
        }

        if (!data.user || !data.session) {
            flowStep('login', 'auth_service_signin_empty_result');
            logLoginTiming("sign_in_empty_result", startedAt);
            return { success: false, error: "Anmeldung fehlgeschlagen." };
        }

        await syncRealtimeAuth(data.session.access_token);

        logger.info("[Auth] Login erfolgreich, User:", data.user.id);

        const metadata = data.user.user_metadata || {};

        // Role: DB first, then metadata fallback (wie Mobile)
        let role: AuthUser["role"] = (metadata.role as AuthUser["role"]) || "patient";

        const { data: userData, error: userError } = await withTimeout(
            supabase
                .from("users")
                .select("role")
                .eq("id", data.user.id)
                .single(),
            AUTH_TIMEOUTS.dbLookupMs,
            "users.role lookup",
        ).catch((err) => {
            logger.warn("[Auth] users.role lookup timeout/failure, nutze Fallback", err);
            return { data: null, error: null } as { data: null; error: null };
        });

        if (userData?.role) {
            role = userData.role as AuthUser["role"];
            logger.info("[Auth] Rolle aus DB:", role);
        } else {
            logger.info(
                "[Auth] DB-Rolle nicht gefunden (RLS?), Metadata-Fallback:",
                role,
                userError?.message,
            );

            // Fallback: infer practice role from ownership/staff relation
            if (role !== "practice") {
                const [ownerRes, staffRes] = await withTimeout(
                    Promise.all([
                        supabase.from("practices").select("id").eq("user_id", data.user.id).limit(1).maybeSingle(),
                        supabase
                            .from("practice_staff")
                            .select("practice_id")
                            .eq("user_id", data.user.id)
                            .eq("is_active", true)
                            .limit(1)
                            .maybeSingle(),
                    ]),
                    AUTH_TIMEOUTS.dbLookupMs,
                    "practice role fallback lookup",
                ).catch((err) => {
                    logger.warn("[Auth] practice role fallback lookup timeout/failure", err);
                    return [{ data: null }, { data: null }] as const;
                });

                if (ownerRes.data?.id || staffRes.data?.practice_id) {
                    role = "practice";
                    logger.info("[Auth] Rolle als practice inferiert über practices/practice_staff");
                }
            }
        }

        // Only practice role allowed on web
        if (role !== "practice") {
            logger.info("[Auth] Rolle ist", role, "— nicht erlaubt auf Web");
            await supabase.auth.signOut();
            logLoginTiming("role_not_allowed", startedAt);
            return {
                success: false,
                error: `Diese Anwendung ist nur für Praxis-Mitarbeiter (Rolle: "${role}"). Bitte nutze die MediFlow App.`,
            };
        }

        // Resolve practiceId: metadata → practices → practice_staff (DRY helper)
        const resolvedPracticeId = await traceAsync(
            'auth.resolvePracticeId',
            () => resolvePracticeId(data.user.id, metadata),
            { timeoutMs: AUTH_TIMEOUTS.dbLookupMs },
        );
        const practiceId = resolvedPracticeId || data.user.id;

            const displayName = await traceAsync(
                'auth.resolvePracticeDisplayName',
                () => resolvePracticeDisplayName(data.user.id, practiceId, metadata),
                { timeoutMs: AUTH_TIMEOUTS.dbLookupMs },
            );
        if (!resolvedPracticeId) {
            logger.warn("[Auth] practiceId Fallback auf user.id:", data.user.id);
            void fireAndForget(
                supabase.auth.updateUser({ data: { practiceId, cached_practice_id: practiceId } }),
                "auth.updateUser cache practiceId fallback",
            );
        }

        // Update last login (fire-and-forget)
        if (practiceId) {
            supabase
                .from("practice_staff")
                .update({ last_login_at: new Date().toISOString() })
                .eq("user_id", data.user.id)
                .then(() => {});
        }

        const user: AuthUser = {
            id: data.user.id,
            email: data.user.email || "",
                firstName: displayName.firstName,
                lastName: displayName.lastName,
            phone: metadata.phone as string | undefined,
            role,
            isVerified: data.user.email_confirmed_at !== null,
            avatarUrl: metadata.avatar_url as string | undefined,
            createdAt: new Date(data.user.created_at),
            practiceId,
        };

        logger.info("[Auth] Login komplett:", { role, practiceIdPresent: Boolean(practiceId) });
        flowStep('login', 'auth_service_login_success', { role, hasPracticeId: Boolean(practiceId) });
        logLoginTiming("success", startedAt);
        return { success: true, user };
    } catch (err) {
        logger.error("[Auth] Unerwarteter Fehler:", err);
        if (isTimeoutError(err)) {
            flowStep('login', 'auth_service_timeout');
            logLoginTiming("timeout", startedAt);
            return {
                success: false,
                error: "Login dauert zu lange. Bitte erneut versuchen. Falls das Problem bleibt, Support kontaktieren.",
            };
        }
        flowStep('login', 'auth_service_unexpected_error', {
            reason: (err as Error)?.message || 'unknown_error',
        });
        logLoginTiming("unexpected_error", startedAt);
        return { success: false, error: "Ein unerwarteter Fehler ist aufgetreten." };
    }
}

/**
 * Passwort-Reset E-Mail senden (Supabase Auth → SMTP/Strato)
 */
export async function requestPasswordReset(
    email: string,
): Promise<{ success: boolean; error?: string }> {
    if (!isSupabaseConfigured()) {
        return { success: false, error: "Supabase ist nicht konfiguriert." };
    }

    try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/login`,
        });

        if (error) {
            logger.error("[Auth] Reset-Mail Fehler:", error.message);
            return { success: false, error: mapResetError(error.message) };
        }

        logger.info("[Auth] Reset-Mail gesendet an:", email);
        return { success: true };
    } catch {
        return { success: false, error: "E-Mail konnte nicht gesendet werden." };
    }
}

/**
 * Logout — clear Supabase session
 */
export async function logout(): Promise<void> {
    await supabase.auth.signOut();
}

/**
 * Restore session — check if user is already logged in
 */
export async function restoreSession(): Promise<AuthUser | null> {
    if (!isSupabaseConfigured()) return null;

    try {
        const accessToken = await ensureValidSession();
        if (!accessToken) return null;

        const {
            data: { session },
        } = await withTimeout(
            supabase.auth.getSession(),
            AUTH_TIMEOUTS.dbLookupMs,
            "auth.getSession",
        );
        if (!session?.user) return null;

        await syncRealtimeAuth(session.access_token);

        const user = session.user;
        const metadata = user.user_metadata || {};

        // Role: DB first, metadata fallback
        let role: AuthUser["role"] = (metadata.role as AuthUser["role"]) || "patient";

        const { data: userData } = await withTimeout(
            supabase
                .from("users")
                .select("role")
                .eq("id", user.id)
                .single(),
            AUTH_TIMEOUTS.dbLookupMs,
            "restoreSession users.role lookup",
        ).catch((err) => {
            logger.warn("[Auth] restoreSession users.role timeout/failure", err);
            return { data: null } as { data: null };
        });

        if (userData?.role) {
            role = userData.role as AuthUser["role"];
        } else if (role !== "practice") {
            const [ownerRes, staffRes] = await withTimeout(
                Promise.all([
                    supabase.from("practices").select("id").eq("user_id", user.id).limit(1).maybeSingle(),
                    supabase
                        .from("practice_staff")
                        .select("practice_id")
                        .eq("user_id", user.id)
                        .eq("is_active", true)
                        .limit(1)
                        .maybeSingle(),
                ]),
                AUTH_TIMEOUTS.dbLookupMs,
                "restoreSession practice role fallback lookup",
            ).catch((err) => {
                logger.warn("[Auth] restoreSession practice role fallback timeout/failure", err);
                return [{ data: null }, { data: null }] as const;
            });

            if (ownerRes.data?.id || staffRes.data?.practice_id) {
                role = "practice";
            }
        }

        if (role !== "practice") return null;

        // Resolve practiceId: metadata → practices → practice_staff (DRY helper)
        const resolvedPracticeId = await resolvePracticeId(user.id, metadata);
        const practiceId = resolvedPracticeId || user.id;

            const displayName = await resolvePracticeDisplayName(user.id, practiceId, metadata);
        if (!resolvedPracticeId) {
            logger.warn("[Auth] Restore practiceId Fallback auf user.id:", user.id);
            void fireAndForget(
                supabase.auth.updateUser({ data: { practiceId, cached_practice_id: practiceId } }),
                "auth.updateUser cache practiceId restore fallback",
            );
        }

        return {
            id: user.id,
            email: user.email || "",
                firstName: displayName.firstName,
                lastName: displayName.lastName,
            phone: metadata.phone as string | undefined,
            role,
            isVerified: user.email_confirmed_at !== null,
            avatarUrl: metadata.avatar_url as string | undefined,
            createdAt: new Date(user.created_at),
            practiceId,
        };
    } catch {
        return null;
    }
}

/**
 * Subscribe to auth state changes (token refresh, logout)
 */
export function onAuthStateChange(
    callback: (
        user: AuthUser | null,
        meta?: { shouldClearAuthState?: boolean; event?: string },
    ) => void,
) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === "SIGNED_OUT" || !session?.user) {
            callback(null, { shouldClearAuthState: true, event });
            return;
        }

        if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN") {
            try {
                await syncRealtimeAuth(session.access_token);
                const user = await withTimeout(
                    restoreSession(),
                    AUTH_TIMEOUTS.dbLookupMs * 2,
                    "restoreSession onAuthStateChange",
                );
                if (user) {
                    callback(user, { shouldClearAuthState: false, event });
                    return;
                }

                logger.warn("[Auth] restoreSession via onAuthStateChange returned null; keeping current auth state", {
                    event,
                });
            } catch (err) {
                logger.warn("[Auth] restoreSession via onAuthStateChange timeout/failure", err);
            }
        }
    });
}

function mapAuthError(message: string): string {
    if (message.includes("Invalid login credentials")) {
        return "E-Mail oder Passwort falsch.";
    }
    if (message.includes("Email not confirmed")) {
        return "E-Mail noch nicht bestätigt. Bitte prüfe dein Postfach.";
    }
    if (message.includes("Too many requests")) {
        return "Zu viele Anmeldeversuche. Bitte warte einen Moment.";
    }
    if (message.includes("User not found")) {
        return "Kein Konto mit dieser E-Mail gefunden.";
    }
    return `Anmeldung fehlgeschlagen: ${message}`;
}

function mapResetError(message: string): string {
    if (message.includes("Too many requests") || message.includes("rate limit")) {
        return "Zu viele Anfragen. Bitte warte einen Moment.";
    }
    if (message.includes("User not found")) {
        return "Kein Konto mit dieser E-Mail gefunden.";
    }
    return "E-Mail konnte nicht gesendet werden. Bitte versuche es erneut.";
}

/**
 * Resolve practiceId: metadata → practices → practice_staff
 * DRY: Used by both login() and restoreSession()
 */
async function resolvePracticeId(
    userId: string,
    metadata: Record<string, unknown>,
): Promise<string | undefined> {
    // 1. Cached in user metadata
    let practiceId =
        (metadata.practiceId as string | undefined) ||
        (metadata.cached_practice_id as string | undefined) ||
        (metadata.practice_id as string | undefined);
    if (practiceId) {
        const [ownerPracticeRes, staffPracticeRes] = await withTimeout(
            Promise.all([
                supabase
                    .from("practices")
                    .select("id")
                    .eq("id", practiceId)
                    .eq("user_id", userId)
                    .maybeSingle(),
                supabase
                    .from("practice_staff")
                    .select("practice_id")
                    .eq("practice_id", practiceId)
                    .eq("user_id", userId)
                    .eq("is_active", true)
                    .maybeSingle(),
            ]),
            AUTH_TIMEOUTS.dbLookupMs,
            "resolvePracticeId metadata validation",
        ).catch((err) => {
            logger.warn("[Auth] resolvePracticeId metadata validation timeout/failure", err);
            return [{ data: null }, { data: null }] as const;
        });

        if (ownerPracticeRes.data?.id || staffPracticeRes.data?.practice_id) {
            logger.info("[Auth] practiceId aus Metadata:", practiceId);
            return practiceId;
        }

        logger.warn("[Auth] Ignoriere ungueltige metadata practiceId:", practiceId);
        practiceId = undefined;
    }

    const [ownerPracticeRes, directPracticeRes, staffRes] = await withTimeout(
        Promise.all([
            supabase.from("practices").select("id").eq("user_id", userId).maybeSingle(),
            supabase.from("practices").select("id").eq("id", userId).maybeSingle(),
            supabase.from("practice_staff").select("practice_id").eq("user_id", userId).limit(1).maybeSingle(),
        ]),
        AUTH_TIMEOUTS.dbLookupMs,
        "resolvePracticeId lookups",
    ).catch((err) => {
        logger.warn("[Auth] resolvePracticeId timeout/failure", err);
        return [{ data: null }, { data: null }, { data: null }] as const;
    });

    if (ownerPracticeRes.data?.id) {
        practiceId = ownerPracticeRes.data.id;
        logger.info("[Auth] practiceId aus practices-Tabelle:", practiceId);
        void fireAndForget(
            supabase.auth.updateUser({ data: { practiceId, cached_practice_id: practiceId } }),
            "auth.updateUser cache owner practiceId",
        );
        return practiceId;
    }

    if (directPracticeRes.data?.id) {
        practiceId = directPracticeRes.data.id;
        logger.info("[Auth] practiceId via practices.id=user_id Fallback:", practiceId);
        void fireAndForget(
            supabase.auth.updateUser({ data: { practiceId, cached_practice_id: practiceId } }),
            "auth.updateUser cache direct practiceId",
        );
        return practiceId;
    }

    if (staffRes.data?.practice_id) {
        practiceId = staffRes.data.practice_id;
        logger.info("[Auth] practiceId aus practice_staff:", practiceId);
        void fireAndForget(
            supabase.auth.updateUser({ data: { practiceId, cached_practice_id: practiceId } }),
            "auth.updateUser cache staff practiceId",
        );
        return practiceId;
    }

    logger.warn("[Auth] Keine practiceId gefunden für User:", userId);
    return undefined;
}

async function resolvePracticeDisplayName(
    userId: string,
    practiceId: string,
    metadata: Record<string, unknown>,
): Promise<{ firstName: string; lastName: string }> {
    try {
        const { data: staff } = await withTimeout(
            supabase
                .from("practice_staff")
                .select("first_name, last_name")
                .eq("user_id", userId)
                .eq("is_active", true)
                .limit(1)
                .maybeSingle(),
            AUTH_TIMEOUTS.dbLookupMs,
            "resolvePracticeDisplayName staff",
        );

        const staffFirstName = (staff?.first_name as string | undefined)?.trim() || "";
        const staffLastName = (staff?.last_name as string | undefined)?.trim() || "";
        if (staffFirstName || staffLastName) {
            return {
                firstName: staffFirstName || "Praxis",
                lastName: staffLastName,
            };
        }

        const { data: practice } = await withTimeout(
            supabase.from("practices").select("name").eq("id", practiceId).maybeSingle(),
            AUTH_TIMEOUTS.dbLookupMs,
            "resolvePracticeDisplayName practice",
        );

        const practiceName = (practice?.name as string | undefined)?.trim() || "";
        if (practiceName) {
            return {
                firstName: practiceName,
                lastName: "",
            };
        }
    } catch {
        // fallback below
    }

    const metaFirstName = (metadata.first_name as string | undefined)?.trim() || "";
    const metaLastName = (metadata.last_name as string | undefined)?.trim() || "";
    if (metaFirstName || metaLastName) {
        return {
            firstName: metaFirstName || "Praxis",
            lastName: metaLastName,
        };
    }

    return { firstName: "Praxis", lastName: "Team" };
}

function fireAndForget<T>(promise: Promise<T>, label: string): void {
    void withTimeout(promise, AUTH_TIMEOUTS.metadataWriteMs, label).catch((err) => {
        logger.warn(`[Auth] Non-blocking operation failed: ${label}`, err);
    });
}

function withTimeout<T>(promise: PromiseLike<T> | T, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(new Error(`Timeout: ${label} (${ms}ms)`));
        }, ms);

        Promise.resolve(promise).then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });
}

function isTimeoutError(err: unknown): boolean {
    return err instanceof Error && err.message.startsWith("Timeout:");
}

function logLoginTiming(
    outcome:
        | "success"
        | "timeout"
        | "sign_in_error"
        | "sign_in_empty_result"
        | "role_not_allowed"
        | "not_configured"
        | "unexpected_error",
    startedAt: number,
): void {
    const durationMs = Date.now() - startedAt;
    const payload = { outcome, durationMs };

    if (durationMs >= 2000) {
        logger.warn("[Auth] Login slow path", payload);
        return;
    }

    logger.info("[Auth] Login timing", payload);
}
